import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import useMutation, { METHOD } from 'hook/useMutation';

import { API_PATH } from 'common/constants/services';
import { AxiosError } from 'axios';
import { Box } from '@mui/system';
import { Icon } from '@iconify/react';
import ListScrollComponent from 'components/ListScrollComponent';
import Loading from 'components/Loading';
import QueryString from 'qs';
import arrowIosForwardOutlineIcon from '@iconify/icons-eva/arrow-ios-forward-outline';
import { queryClient } from 'App';
import { toast } from 'react-toastify';
import useQuery from 'hook/useQuery';

interface IProps {
  isOpen: boolean;
  close: () => void;
}

export function WorkoutMergeModal({ isOpen, close }: IProps) {
  const workouts = useQuery<WorkoutResponse>(API_PATH.WORKOUTS, {
    limit: 1000,
  });
  const [workoutFromString, setWorkoutFromString] = useState('');
  const [workoutToString, setWorkoutToString] = useState('');
  const [workoutFrom, setWorkoutFrom] = useState<Workout | null>(null);
  const [workoutTo, setWorkoutTo] = useState<Workout | null>(null);
  const [userList, setUserList] = useState<number[]>([]);
  const [historyList, setHistoryList] = useState<number[]>([]);
  const [routineList, setRoutineList] = useState<number[]>([]);
  const [challengeConditionList, setConditionList] = useState<number[]>([]);
  const [isMergeProgress, setMergeProgress] = useState(false);
  const [isMigrateTestSucceed, setMigrateTestSucceed] = useState(false);
  const workoutQuery = useQuery<WorkoutResponse>(API_PATH.WORKOUTS);
  const postWorkoutMigrate = useMutation<IPostWorkoutsMigrate>(
    API_PATH.POST_WORKOUT_MIGRATE,
    {
      method: METHOD.POST,
      onSuccess: (res: any) => {
        if (res) {
          toast.success('운동 마이그레이션이 완료되었습니다.');
          close();

          queryClient.invalidateQueries([API_PATH.WORKOUTS]);
        }
      },
      onError: (error: AxiosError) => {
        const { response, message } = error;

        alert(`에러 - ${message}`);
        toast.error(message);
        setMergeProgress(false);
      },
    },
  );
  const postDryRunWorkoutMigrate = useMutation<IPostWorkoutsMigrate>(
    API_PATH.POST_WORKOUT_MIGRATE,
    {
      method: METHOD.POST,
      onSuccess: (res) => {
        if (res) {
          toast.success(
            '영향력 테스트가 완료되었습니다. 영향이가는 데이터를 확인 후 마이그레이션을 작동 시켜주세요',
          );
          setMergeProgress(false);
          setMigrateTestSucceed(true);
          setUserList((res as any).data.userList);
          setHistoryList((res as any).data.historyList);
          setRoutineList((res as any).data.challengeRoutineList);
          setConditionList((res as any).data.challengeConditionList);
        }
      },
      onError: (error: AxiosError) => {
        const { response, message } = error;

        alert(`에러 - ${message}`);
        toast.error(message);
        setMergeProgress(false);
      },
    },
  );

  const handleCloseModal = () => {
    close();
  };

  const onClickSave = () => {
    if (workoutFrom && workoutTo) {
      const params = {
        workout_from: workoutFrom.id,
        workout_to: workoutTo.id,
        dry_run: false,
      };
      setMergeProgress(true);

      if (window.confirm('Migrate 하시겠습니까?')) {
        postWorkoutMigrate.mutate(params);
      }
    } else {
      alert('머지할 운동을 선택해 주세요.');
    }
  };

  const setWorkoutFromData = (workout: Workout) => {
    console.log(workout);
    setWorkoutFrom(workout);
    setWorkoutFromString(workout?.name || '');
  };

  const setWorkoutToData = (workout: Workout) => {
    setWorkoutTo(workout);
    setWorkoutToString(workout?.name || '');
  };

  const onClickMigrateDryRun = () => {
    if (workoutFrom && workoutTo) {
      const params = {
        workout_from: workoutFrom.id,
        workout_to: workoutTo.id,
        dry_run: true,
      };

      setUserList([]);
      setHistoryList([]);
      setMergeProgress(true);

      postDryRunWorkoutMigrate.mutate(params);
    } else {
      alert('머지할 운동을 선택해 주세요.');
    }
  };

  const isLoading = useMemo(
    () => postWorkoutMigrate.isLoading || isMergeProgress,
    [postWorkoutMigrate, isMergeProgress],
  );

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      keepMounted
      open={isOpen}
      aria-labelledby='keep-mounted-modal-title'
      aria-describedby='keep-mounted-modal-description'
    >
      <DialogContent>
        <Loading isLoading={isLoading} />
        <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
          <Box sx={{ minWidth: 350 }}>
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant='button'>From</Typography>
                <Autocomplete
                  // value={workoutFrom}
                  inputValue={workoutFromString}
                  // autoSelect
                  autoHighlight
                  getOptionLabel={(option) => option.name || ''}
                  groupBy={(option) => option.part.name}
                  sx={{ width: '100%', flex: 1 }}
                  options={[
                    ...new Set(
                      workouts.getQuery.data?.data.results.sort(
                        (a, b) => -b.name.localeCompare(a.name),
                      ),
                    ),
                  ]}
                  onChange={(_, value) => setWorkoutFromData(value as Workout)}
                  renderInput={(params) => (
                    <TextField
                      autoFocus
                      {...params}
                      size='small'
                      type='text'
                      placeholder='운동명'
                      onChange={(e) => setWorkoutFromString(e.target.value)}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography variant='button'>부위</Typography>
                <TextField
                  value={workoutFrom?.part.name || ''}
                  size='small'
                  sx={{ width: 60 }}
                />
              </Box>
            </Stack>
          </Box>
          <Box sx={{ marginTop: '24px !important' }}>
            <Icon icon={arrowIosForwardOutlineIcon} fontSize={16} />
          </Box>
          <Box sx={{ minWidth: 350 }}>
            <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant='button'>To</Typography>
                <Autocomplete
                  // value={workoutTo}
                  inputValue={workoutToString}
                  // autoSelect
                  autoHighlight
                  getOptionLabel={(option) => option.name || ''}
                  groupBy={(option) => option.part.name}
                  sx={{ width: '100%', flex: 1 }}
                  options={[
                    ...new Set(
                      workouts.getQuery.data?.data.results.sort(
                        (a, b) => -b.name.localeCompare(a.name),
                      ),
                    ),
                  ]}
                  onChange={(_, value) => setWorkoutToData(value as Workout)}
                  renderInput={(params) => (
                    <TextField
                      autoFocus
                      {...params}
                      size='small'
                      type='text'
                      placeholder='운동명'
                      onChange={(e) => setWorkoutToString(e.target.value)}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography variant='button'>부위</Typography>
                <TextField
                  value={workoutTo?.part.name || ''}
                  size='small'
                  sx={{ width: 60 }}
                />
              </Box>
            </Stack>
          </Box>
          <Box>
            <Button
              variant='contained'
              sx={{ marginTop: 1 }}
              color='success'
              onClick={onClickMigrateDryRun}
            >
              영향력 테스트
            </Button>
          </Box>
        </Stack>
        {/* </Stack> */}

        <Divider sx={{ mt: 4, mb: 4 }} />
        <Box>
          <Typography variant='button'>영향가는 항목 리스트</Typography>
          {userList.length > 0 && (
            <ListScrollComponent list={userList} title='사용하는 유저' />
          )}

          {historyList.length > 0 && (
            <ListScrollComponent
              list={historyList}
              title='사용하고 있는 히스토리'
            />
          )}

          {routineList.length > 0 && (
            <ListScrollComponent list={routineList} title='사용중인 Routine' />
          )}

          {challengeConditionList.length > 0 && (
            <ListScrollComponent
              list={challengeConditionList}
              title='사용중인 Condition'
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={handleCloseModal}>
          취소
        </Button>
        <Button
          variant='contained'
          color='success'
          onClick={onClickSave}
          disabled={!isMigrateTestSucceed}
        >
          마이그레이션 하기
        </Button>
      </DialogActions>
    </Dialog>
  );
}
